/* eslint-disable react-hooks/exhaustive-deps */
import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  LuChevronDown,
  LuChevronLeft,
  LuChevronRight,
  LuChevronsUpDown,
  LuEllipsis,
  LuMenu,
  LuPencil,
  LuPlus,
  LuSearch,
  LuTrash2,
} from "react-icons/lu";

import AppHeader from "components/AppHeader";
import DeleteConfirmationDialog from "components/DeleteConfirmationDialog/DeleteConfirmationDialog";
import { ThemedSelect } from "components/FormControls";
import ListingActionMenu from "components/ListingActionMenu";
import { showDraftSavedMessage } from "components/InlineFeedback/DraftSaveConfirmation";
import { ClearableSearchInput } from "components/SearchField/SearchField";
import { Skeleton } from "components/Skeleton";
import { useMaterialUIController } from "context";
import { DashboardNavigation } from "layouts/businessUserDashboard";
import AddItemDialog from "./AddItemDialog";
import { deleteItems, fetchItems, getItemsById } from "utils/api";
import { appConstants } from "utils/appConstants";
import { formatCurrencyAUD } from "utils/formatCurrency";
import { getScreenRight, showResMsg } from "utils/generic";
import { genericMessage } from "utils/genericMessages";

import "./Items.css";

const SCREEN_NAME = "Items";

const getInitials = (name = "") => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "IR";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
};

const getItemType = (item) => {
  const explicitType = item.itemType || item.type || item.productType;
  if (explicitType) return explicitType;
  return String(item.categoryName || "")
    .toLowerCase()
    .includes("service")
    ? "Service"
    : "Product";
};

const getItemDescription = (item) =>
  item.itemDescription || item.description || item.categoryCodeName || "";

const ItemsTableSkeleton = ({ rows = 10 }) => (
  <>
    {Array.from({ length: rows }, (_, index) => (
      <tr className="items-skeleton-row" data-testid="items-skeleton-row" key={index}>
        <td className="items-check-cell">
          <Skeleton width={14} height={14} radius={2} />
        </td>
        <td>
          <Skeleton width="62%" height={12} />
        </td>
        <td>
          <div className="items-skeleton-name">
            <Skeleton width="54%" height={12} />
            <Skeleton width="72%" height={10} />
          </div>
        </td>
        <td>
          <Skeleton width="52%" height={12} />
        </td>
        <td>
          <Skeleton width="42%" height={12} />
        </td>
        <td>
          <Skeleton variant="pill" width={48} height={20} />
        </td>
        <td className="items-price-cell">
          <Skeleton className="items-skeleton-price" width={72} height={14} />
        </td>
        <td className="items-action-cell">
          <Skeleton width={18} height={6} radius={3} />
        </td>
      </tr>
    ))}
  </>
);

export default function Items({
  initialAddDialog = false,
  initialEditDialog = false,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id: routeItemId } = useParams();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const [loginDetails] = useState(() =>
    JSON.parse(sessionStorage.getItem("LOGIN_DETAILS") || "{}")
  );

  const profile = useMemo(() => {
    const organisation =
      loginDetails.companyName ||
      loginDetails.businessName ||
      loginDetails.organisationName ||
      loginDetails.fullName ||
      "Invoice & Recover";
    const role = loginDetails.roleName || loginDetails.userType || "Owner";
    const userName =
      loginDetails.fullName ||
      loginDetails.name ||
      `${loginDetails.firstName || ""} ${loginDetails.lastName || ""}`.trim() ||
      organisation;
    const profileFileName =
      loginDetails.profilePictureFileName || loginDetails.profilePicFileName;

    return {
      organisation,
      role,
      userName,
      planName:
        loginDetails.planName || loginDetails.subscriptionPlan || "SILVER",
      profileImage: profileFileName
        ? `${appConstants.baseURL}user/viewFile?fileName=${encodeURIComponent(
            profileFileName
          )}`
        : "",
    };
  }, [loginDetails]);

  const [navOpen, setNavOpen] = useState(false);
  const [screenAccess, setScreenAccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [activeTab, setActiveTab] = useState("All");
  const [openMenuId, setOpenMenuId] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [pendingDeleteIds, setPendingDeleteIds] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(initialAddDialog);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteHeading, setDeleteHeading] = useState("");
  const [categories, setCategories] = useState([]);
  const [gstParameters, setGstParameters] = useState([]);
  const [referenceDataLoaded, setReferenceDataLoaded] = useState(false);
  const [values, setValues] = useState(() => {
    const query = new URLSearchParams(window.location.search);
    return {
      _id: "",
      selectedRows: [],
      sortingType: 1,
      sortingField: "itemName",
      itemStatus: "All",
      categoryName: "All",
      categoryCodeName: "All",
      durationDate: query.get("duration") || "",
      fromDate: query.get("from") || "",
      toDate: query.get("to") || "",
      page: appConstants.page,
      limit: appConstants.limit,
      filterValues: "",
      searchParameterList: [
        "categoryName",
        "itemName",
        "itemDescription",
        "itemCode",
        "unitOfMeasurement",
        "itemStatus",
        "categoryCodeName",
      ],
      businessUserID: loginDetails.businessUserID,
    };
  });

  const fetchItemRows = useCallback(async () => {
    setLoading(true);
    await fetchItems(
      values,
      (data) => {
        setTotalRows(data?.Result_Output?.totalDocs || 0);
        setRows(data?.Result_Output?.docs || []);
        setLoading(false);
      },
      dispatch
    );
  }, [dispatch, values]);

  const fetchCategories = useCallback(async () => {
    const [categoriesResult, gstResult] = await Promise.allSettled([
      axios.post(`${appConstants.baseURL}items/fetchCategoriesData`, {
        businessUserID: values.businessUserID,
      }),
      axios.post(`${appConstants.baseURL}items/fetchParameterValue`, {}),
    ]);
    setCategories(
      categoriesResult.status === "fulfilled"
        ? categoriesResult.value.data.fetchCategories || []
        : []
    );
    setGstParameters(
      gstResult.status === "fulfilled"
        ? gstResult.value.data.Result_Output?.[0]?.list || []
        : []
    );
    setReferenceDataLoaded(true);
  }, [values.businessUserID]);

  useEffect(() => {
    const loadAccess = async () => {
      const right = await getScreenRight(SCREEN_NAME);
      setScreenAccess(right === "Full Access");
    };
    loadAccess();
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    fetchItemRows();
  }, [fetchItemRows]);

  useEffect(() => {
    if (!initialEditDialog || !routeItemId || !referenceDataLoaded) return;
    getItemsById(
      { _id: routeItemId },
      (data) => {
        const item = data?.Result_Output?.[0];
        if (data?.Result_Status && item) {
          setEditingItem(item);
          return;
        }
        showResMsg(
          data?.Result_Message || "Unable to load this item.",
          2000,
          false
        );
        navigate("/items", { replace: true });
      },
      dispatch
    );
  }, [dispatch, initialEditDialog, navigate, referenceDataLoaded, routeItemId]);

  const visibleRows = useMemo(() => {
    if (activeTab === "All") return rows;
    return rows.filter((row) => getItemType(row) === activeTab);
  }, [activeTab, rows]);

  const currentPage = Number(values.page) || 1;
  const perPage = Number(values.limit) || appConstants.limit;
  const totalPages = Math.max(1, Math.ceil(totalRows / perPage));
  const startRow = totalRows === 0 ? 0 : (currentPage - 1) * perPage + 1;
  const endRow = Math.min(currentPage * perPage, totalRows);
  const pageRowIds = visibleRows.map((row) => row._id);
  const allPageRowsSelected =
    pageRowIds.length > 0 &&
    pageRowIds.every((id) => selectedRows.includes(id));

  const changePage = (page) => {
    if (page < 1 || page > totalPages || page === currentPage) return;
    setSelectedRows([]);
    setValues((current) => ({ ...current, page }));
  };

  const handleSearch = (event) => {
    setValues((current) => ({
      ...current,
      filterValues: event.target.value,
      page: 1,
    }));
  };

  const toggleSort = () => {
    setValues((current) => ({
      ...current,
      sortingField: "itemName",
      sortingType: current.sortingType === 1 ? -1 : 1,
      page: 1,
    }));
  };

  const toggleAllRows = () => {
    if (allPageRowsSelected) {
      setSelectedRows((current) =>
        current.filter((id) => !pageRowIds.includes(id))
      );
      return;
    }
    setSelectedRows((current) => [...new Set([...current, ...pageRowIds])]);
  };

  const toggleRow = (id) => {
    setSelectedRows((current) =>
      current.includes(id)
        ? current.filter((selectedId) => selectedId !== id)
        : [...current, id]
    );
  };

  const requestDelete = (ids) => {
    const targets = Array.isArray(ids) ? ids : [ids];
    setPendingDeleteIds(targets.filter(Boolean));
    setDeleteHeading(
      targets.length > 1
        ? "Would you like to delete the selected items?"
        : "Would you like to delete this item?"
    );
    setOpenMenuId(null);
    setMenuAnchorEl(null);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    await deleteItems(
      { selectedRows: pendingDeleteIds },
      (data) => {
        showResMsg(
          data.Result_Status ? genericMessage.deleted : data.Result_Message,
          1000,
          data.Result_Status
        );
        setSelectedRows([]);
        setPendingDeleteIds([]);
        setDeleteDialogOpen(false);
        fetchItemRows();
      },
      dispatch
    );
  };

  const toggleRowMenu = (event, rowId) => {
    const isOpen = openMenuId === rowId;
    setOpenMenuId(isOpen ? null : rowId);
    setMenuAnchorEl(isOpen ? null : event.currentTarget);
  };

  const closeRowMenu = () => {
    setOpenMenuId(null);
    setMenuAnchorEl(null);
  };

  const finishItemCreate = (item) => {
    const params = new URLSearchParams(window.location.search);
    const source = params.get("new") || params.get("source");
    const sourceID = params.get("id");
    const itemID = item?._id;
    if (!initialAddDialog || !source || !itemID) {
      setAddDialogOpen(false);
      showDraftSavedMessage("Item added successfully");
      fetchItemRows();
      return;
    }
    const target = sourceID
      ? {
          invoice: `/invoices/edit/${sourceID}?item=${itemID}`,
          quotation: `/quotations/edit/${sourceID}?item=${itemID}`,
          rcti: `/rcti/edit/${sourceID}?item=${itemID}`,
          credit: `/creditNote/edit/${sourceID}?item=${itemID}`,
        }[source]
      : {
          invoice: `/invoices/add/?item=${itemID}`,
          quotation: `/quotations/add?type=new&item=${itemID}`,
          rcti: `/rcti/add/?item=${itemID}`,
          credit: `/creditNote/add?type=new&item=${itemID}`,
          expense: `/expenses/addExpenses/?item=${itemID}`,
        }[source];
    showDraftSavedMessage("Item added successfully");
    navigate(target || "/items");
  };

  const sourceReturnPath = (itemID) => {
    const params = new URLSearchParams(window.location.search);
    const source = params.get("source");
    const newSource = params.get("new");
    const sourceID = params.get("id");
    const editTarget = sourceID
      ? {
          invoice: `/invoices/edit/${sourceID}`,
          quotation: `/quotations/edit/${sourceID}`,
          rcti: `/rcti/edit/${sourceID}`,
          credit: `/creditNote/edit/${sourceID}`,
        }[source]
      : null;
    const createTarget = {
      invoice: "/invoices/add/",
      quotation: "/quotations/add?type=new",
      rcti: "/rcti/add/",
      credit: "/creditNote/add?type=new",
      expense: "/expenses/addExpenses/",
    }[newSource];
    const target = editTarget || createTarget || "/items";
    if (!itemID || target === "/items") return target;
    return `${target}${target.includes("?") ? "&" : "?"}item=${itemID}`;
  };

  const closeEditDialog = () => {
    setEditingItem(null);
    if (initialEditDialog) {
      navigate(sourceReturnPath(), { replace: true });
    }
  };

  const finishItemEdit = () => {
    const itemID = editingItem?._id || routeItemId;
    setEditingItem(null);
    showDraftSavedMessage("Item updated successfully");
    if (initialEditDialog) {
      navigate(sourceReturnPath(itemID), { replace: true });
      return;
    }
    fetchItemRows();
  };

  return (
    <div
      className={`items-page business-dashboard${darkMode ? " dark" : ""}`}
      data-theme={darkMode ? "dark" : "light"}
    >
      <DashboardNavigation
        open={navOpen}
        onClose={() => setNavOpen(false)}
        activePath="/items"
        organisation={profile.organisation}
        role={profile.role}
        planName={profile.planName}
      />

      <div className="dashboard-main items-main">
        <AppHeader
          onOpenNav={() => setNavOpen(true)}
          searchPlaceholder="Search invoices, quotes, items"
          userName={profile.userName}
          profileImage={profile.profileImage}
        />

        <main className="items-content">
          <div className="items-compact-org">
            <button type="button" onClick={() => setNavOpen(true)}>
              <LuMenu />
            </button>
            <span>invoice &amp; recover</span>
            <div>
              <i>{getInitials(profile.organisation)}</i>
              <strong>
                {profile.organisation}
                <small>{profile.role}</small>
              </strong>
              <LuChevronsUpDown />
            </div>
          </div>

          <header className="items-page-header">
            <div>
              <h1>Items</h1>
              <p>
                Manage products and services used across quotes and invoices.
              </p>
            </div>
            <div className="items-header-actions">
              <button type="button" className="items-import-button">
                Import
              </button>
              {screenAccess ? (
                <button
                  type="button"
                  className="items-add-button"
                  onClick={() => setAddDialogOpen(true)}
                >
                  <LuPlus />
                  Add item
                </button>
              ) : (
                <button type="button" className="items-add-button" disabled>
                  <LuPlus />
                  Add item
                </button>
              )}
            </div>
          </header>

          <section className="items-workspace">
            <div className="items-tabs" role="tablist" aria-label="Item type">
              {[
                ["All", "All items"],
                ["Product", "Products"],
                ["Service", "Services"],
              ].map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === value}
                  className={activeTab === value ? "active" : ""}
                  onClick={() => {
                    setActiveTab(value);
                    setSelectedRows([]);
                  }}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="items-controls">
              <label className="items-search">
                <LuSearch />
                <ClearableSearchInput
                  type="text"
                  value={values.filterValues}
                  onChange={handleSearch}
                  placeholder="Search item name, description or code"
                />
              </label>
              <div className="items-filter-wrap">
                <ThemedSelect
                  className="items-filter-select"
                  dark={darkMode}
                  aria-label="Account"
                  value={values.categoryCodeName}
                  displayValue={values.categoryCodeName === "All" ? "Account" : values.categoryCodeName}
                  onChange={(event) => setValues((current) => ({
                    ...current, categoryCodeName: event.target.value, page: 1,
                  }))}
                >
                  <option value="All">All</option>
                  {categories.map((category) => (
                    <option key={category._id || category.categoryCodeName} value={category.categoryCodeName}>
                      {category.categoryCodeName}
                    </option>
                  ))}
                </ThemedSelect>
                <ThemedSelect
                  className="items-filter-select"
                  dark={darkMode}
                  aria-label="Status"
                  value={values.itemStatus}
                  displayValue={values.itemStatus === "All" ? "Status" : values.itemStatus}
                  onChange={(event) => setValues((current) => ({
                    ...current, itemStatus: event.target.value, page: 1,
                  }))}
                >
                  <option value="All">All</option>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </ThemedSelect>
                {(values.categoryCodeName !== "All" || values.itemStatus !== "All" || Boolean(values.filterValues.trim())) && (
                  <button
                    type="button"
                    className="items-filter-button"
                    onClick={() => setValues((current) => ({
                      ...current, categoryCodeName: "All", itemStatus: "All", filterValues: "", page: 1,
                    }))}
                  >
                    Clear filters
                  </button>
                )}
              </div>
            </div>

            <div className="items-table-card">
              <div className="items-table-toolbar">
                <strong>
                  {selectedRows.length
                    ? `${selectedRows.length} item${
                        selectedRows.length === 1 ? "" : "s"
                      } selected`
                    : `${totalRows} item${totalRows === 1 ? "" : "s"}`}
                </strong>
                {selectedRows.length && screenAccess ? (
                  <button
                    type="button"
                    className="items-delete-selected"
                    onClick={() => requestDelete(selectedRows)}
                  >
                    <LuTrash2 />
                    Delete
                  </button>
                ) : (
                  <button
                    type="button"
                    className="items-sort"
                    onClick={toggleSort}
                  >
                    Name {values.sortingType === 1 ? "A–Z" : "Z–A"}
                    <LuChevronDown />
                  </button>
                )}
              </div>

              <div className="items-table-scroll">
                <table className="items-table">
                  <thead>
                    <tr>
                      <th className="items-check-cell">
                        <input
                          type="checkbox"
                          checked={allPageRowsSelected}
                          onChange={toggleAllRows}
                          aria-label="Select all items on this page"
                        />
                      </th>
                      <th>Item code</th>
                      <th>Item</th>
                      <th>Type</th>
                      <th>Unit</th>
                      <th>Status</th>
                      <th className="items-price-cell">Price · GST incl.</th>
                      <th className="items-action-cell">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <ItemsTableSkeleton rows={10} />
                    ) : visibleRows.length ? (
                      visibleRows.map((row) => {
                        const status = row.itemStatus || "Inactive";
                        const selected = selectedRows.includes(row._id);
                        return (
                          <tr
                            key={row._id}
                            className={selected ? "selected" : ""}
                          >
                            <td className="items-check-cell">
                              <input
                                type="checkbox"
                                checked={selected}
                                onChange={() => toggleRow(row._id)}
                                aria-label={`Select ${
                                  row.itemName || row.itemCode
                                }`}
                              />
                            </td>
                            <td>
                              <button
                                type="button"
                                className="items-code-link"
                                title={row.itemCode || ""}
                                onClick={() => setEditingItem(row)}
                              >
                                {row.itemCode || "—"}
                              </button>
                            </td>
                            <td>
                              <strong
                                className="items-name"
                                title={row.itemName || row.itemDescription || ""}
                              >
                                {row.itemName || row.itemDescription || "—"}
                              </strong>
                              <span
                                className="items-description"
                                title={getItemDescription(row)}
                              >
                                {getItemDescription(row)}
                              </span>
                            </td>
                            <td>{getItemType(row)}</td>
                            <td>{row.unitOfMeasurement || "Each"}</td>
                            <td>
                              <span
                                className={`items-status items-status-${status.toLowerCase()}`}
                              >
                                {status}
                              </span>
                            </td>
                            <td className="items-price-cell">
                              $
                              {formatCurrencyAUD(
                                row.itemPrice ?? row.unitPrice ?? row.price ?? 0
                              )}
                            </td>
                            <td className="items-action-cell">
                              <div className="items-row-menu">
                                <button
                                  type="button"
                                  className="listing-action-trigger"
                                  onClick={(event) =>
                                    toggleRowMenu(event, row._id)
                                  }
                                  aria-label={`Actions for ${
                                    row.itemName || row.itemCode
                                  }`}
                                  aria-expanded={openMenuId === row._id}
                                >
                                  <LuEllipsis />
                                </button>
                                {openMenuId === row._id && (
                                  <ListingActionMenu
                                    anchorEl={menuAnchorEl}
                                    label={`Actions for ${
                                      row.itemName || row.itemCode
                                    }`}
                                    onClose={closeRowMenu}
                                  >
                                    <button
                                      type="button"
                                      onClick={() => {
                                        closeRowMenu();
                                        setEditingItem(row);
                                      }}
                                    >
                                      <LuPencil />
                                      Edit
                                    </button>
                                    <button
                                      type="button"
                                      className="danger"
                                      disabled={!screenAccess}
                                      onClick={() => requestDelete(row._id)}
                                    >
                                      <LuTrash2 />
                                      Delete
                                    </button>
                                  </ListingActionMenu>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="8" className="items-empty-state">
                          No items found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <footer className="items-pagination">
                <span>
                  Showing {startRow}–{endRow} of {totalRows} items
                </span>
                <div>
                  <button
                    type="button"
                    onClick={() => changePage(currentPage - 1)}
                    disabled={currentPage <= 1}
                    aria-label="Previous page"
                  >
                    <LuChevronLeft />
                  </button>
                  <button
                    type="button"
                    onClick={() => changePage(currentPage + 1)}
                    disabled={currentPage >= totalPages}
                    aria-label="Next page"
                  >
                    <LuChevronRight />
                  </button>
                </div>
              </footer>
            </div>
          </section>
        </main>
      </div>

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        handleDelete={handleDelete}
        handleCancel={() => {
          setPendingDeleteIds([]);
          setDeleteDialogOpen(false);
        }}
        closeModalClick={() => {
          setPendingDeleteIds([]);
          setDeleteDialogOpen(false);
        }}
        message={deleteHeading}
      />
      {addDialogOpen && (
        <AddItemDialog
          businessUserID={loginDetails.businessUserID}
          categories={categories}
          darkMode={darkMode}
          onClose={() => setAddDialogOpen(false)}
          onSaved={finishItemCreate}
        />
      )}
      {editingItem && (
        <AddItemDialog
          key={editingItem._id}
          businessUserID={loginDetails.businessUserID}
          categories={categories}
          darkMode={darkMode}
          gstParameters={gstParameters}
          item={editingItem}
          onClose={closeEditDialog}
          onSaved={finishItemEdit}
          readOnly={!screenAccess}
          source={new URLSearchParams(window.location.search).get("source")}
          sourceID={new URLSearchParams(window.location.search).get("id")}
        />
      )}
    </div>
  );
}
